import { useContext, useEffect, useState } from "react";
import PageTitle from "../PageTitle";
import AddIcon from "@mui/icons-material/Add";
import EnhancedTable from "../../layouts/table";
import { Box } from "@mui/system";
import {
  Chip,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import FilterForm from "./FilterForm";
import AdminModal from "./AdminModal";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import toast from "react-hot-toast";
import LoadingTableRows from "../LoadingTableRows";
import { AppContext } from "../../scripts/context/App";
import ConfirmationModal from "../ConfirmationModal";

interface IFilter {
  status: boolean;
  data: {
    permissions: string;
    full_name: string;
    email: string;
    roles: string[];
    status: string;
  };
}

interface INewAdmin {
  status: boolean;
  edit: boolean;
  data: {
    first_name: string;
    last_name: string;
    email: string;
    mobile: string;
    roles: string[];
    status: boolean;
    permissions: string[];
  };
}

const initFilterData: IFilter = {
  status: true,
  data: {
    permissions: "",
    full_name: "",
    email: "",
    roles: [],
    status: "",
  },
};

const initAdminState: INewAdmin = {
  status: false,
  edit: false,
  data: {
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    roles: [],
    status: true,
    permissions: [],
  },
};

export default function AdminsManagement() {
  const { user } = useContext(AppContext);
  const [paginationData, setPaginationDatas] = useState<any>({
    page: 1,
    pagesCount: -1,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    tableHead: [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "email",
        numeric: false,
        disablePadding: false,
        label: "Email",
      },
      {
        id: "phone",
        numeric: false,
        disablePadding: false,
        label: "Phone",
      },
      {
        id: "status",
        numeric: false,
        disablePadding: false,
        label: "Status",
      },
      {
        id: "actions",
        numeric: false,
        disablePadding: false,
        label: "Actions",
      },
    ],
    tableItems: [],
  });
  const setPaginationData = (data: any) =>
    setPaginationDatas({ ...paginationData, ...data });
  const [filter, setFilter] = useState<IFilter>(initFilterData);
  const [admin, setAdmin] = useState<INewAdmin>(initAdminState);
  const [order, setOrder] = useState<any>({
    order: "asc",
    orderBy: "name",
  });
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState({
    status: false,
    id: "",
  });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    getUsers({
      name: filter.data.full_name,
      email: filter.data.email,
      roles: filter.data.roles,
      status: filter.data.status,
      permission: filter.data.permissions,
      ...(order.orderBy
        ? {
          sortBy: order.orderBy,
          sortOrder: order.order,
        }
        : {}),
      page: 1,
      perPage: event.target.value,
    });
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    getUsers({
      name: filter.data.full_name,
      email: filter.data.email,
      roles: filter.data.roles,
      status: filter.data.status,
      permission: filter.data.permissions,
      ...(order.orderBy
        ? {
          sortBy: order.orderBy,
          sortOrder: order.order,
        }
        : {}),
      page: newPage,
      perPage: paginationData.rowsPerPage,
    });
  };

  const getUsers = async (params?: any) => {
    try {
      const {
        data: { data },
      } = await axios.get("/users/get-users", { params });

      setPaginationData({
        tableItems: data.data.map((item: any) => ({
          ...item,
          permissions: item.permissions,
        })),
        pagesCount: data.total,
        rowsPerPage: Number(data.per_page),
        page: data.current_page,
      });
    } catch (error: any) {
      toast.error(
        error.response.data.message || "Something went wrong, please try again"
      );

      console.log(error);
    }
  };

  const addNewUser = (data: any) =>
    user.permissions.includes("add_system_user")
      ? setPaginationData({ tableItems: [...paginationData.tableItems, data] })
      : toast.error("You dont have permissions to do this action");

  const updateUser = (data: any) => {
    if (!user.permissions.includes("edit_system_user"))
      return toast.error("You dont have permissions to do this action");

    const index = paginationData.tableItems.findIndex(
      (item: any) => item.id === data.id
    );
    setPaginationData({
      tableItems: [
        ...paginationData.tableItems.slice(0, index),
        data,
        ...paginationData.tableItems.slice(index + 1),
      ],
    });
  };
  const deleteUser = async (user_id: string) => {
    if (!user.permissions.includes("delete_system_user"))
      return toast.error("You dont have permissions to do this action");
    try {
      await axios.delete("/users/delete", {
        data: {
          user_id,
        },
      });

      toast.success("Admin deleted successfully");
      setPaginationData({
        tableItems: paginationData.tableItems.filter(
          (item: any) => item.id !== user_id
        ),
      });
      setConfirmDelete({ status: false, id: "" });
    } catch (error) {
      toast.error(
        "Error while deleting admin, please try again later or contact support"
      );
      console.log(error);
      setConfirmDelete({ status: false, id: "" });
    }
  };

  const getInitData = async () => {
    await getUsers();

    setLoading(false);
  };

  useEffect(() => {
    if (!user.permissions.includes("view_system_user")) return;

    getInitData();
  }, []);

  if (!user.permissions.includes("view_system_user"))
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <h3>You dont have permissions to view this page</h3>
      </Box>
    );

  return (
    <>
      <PageTitle
        title="Admins Management"
        description="Manage your admins"
        actionLabel="Add new admin"
        action={() => {
          if (!user.permissions.includes("add_system_user"))
            return toast.error("You dont have permissions to do this action");

          setAdmin({ ...admin, status: true, edit: false });
        }}
        startIcon={<AddIcon />}
      />
      <AdminModal
        updateUser={updateUser}
        addNewUser={addNewUser}
        data={admin}
        setData={(object: any) =>
          setAdmin({
            ...admin,
            data: { ...admin.data, ...object },
          })
        }
        handleClose={() => {
          setAdmin(initAdminState);
        }}
      />
      <ConfirmationModal
        open={confirmDelete.status}
        onClose={() => setConfirmDelete({ ...confirmDelete, status: false })}
        onConfirm={() => deleteUser(confirmDelete.id)}
        title="Are you sure?"
        message={
          "This action cannot be undone, please confirm to continue"
        }
      />
      <Box sx={{ p: 3 }}>
        <EnhancedTable
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          TableRows={
            loading ? (
              <LoadingTableRows
                elementsCount={paginationData.tableHead.length}
              />
            ) : paginationData.tableItems.length > 0 ? (
              paginationData.tableItems.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{`${row.first_name} ${row.last_name}`}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.mobile}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        typeof row.status === "string"
                          ? row.status
                          : row.status
                            ? "Active"
                            : "Inactive"
                      }
                      variant="outlined"
                      color={row.status === "Active" ? "success" : "error"}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit System User">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          if (!user.permissions.includes("edit_system_user"))
                            return toast.error(
                              "You dont have permissions to do this action"
                            );

                          setAdmin({ status: true, edit: true, data: row });
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete System User">
                      <IconButton
                        color="error"
                        onClick={() => {
                          if (!user.permissions.includes("delete_system_user"))
                            return toast.error(
                              "You dont have permissions to do this action"
                            );

                          setConfirmDelete({
                            status: true,
                            id: row.id
                          })
                          // deleteUser(row.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={paginationData.tableHead.length}>
                  <Typography
                    sx={{ textAlign: "center" }}
                    variant="body2"
                    color="textPrimary"
                  >
                    No data found
                  </Typography>
                </TableCell>
              </TableRow>
            )
          }
          handleRequestSortHeader={(orderFunc, orderByFunc) => {
            if (!orderByFunc) return;

            getUsers({
              name: filter.data.full_name,
              email: filter.data.email,
              roles: filter.data.roles,
              status: filter.data.status,
              permission: filter.data.permissions,
              sortBy: orderByFunc,
              sortOrder: orderFunc,
              page: 1,
              perPage: paginationData.rowsPerPage,
            });

            setOrder({
              order: orderFunc,
              orderBy: orderByFunc,
            });
          }}
          {...paginationData}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() =>
                  setFilter({
                    ...filter,
                    status: !filter.status,
                  })
                }
              >
                {filter.status ? <FilterListIcon /> : <FilterListOffIcon />}
              </IconButton>
              <Typography variant="h6">Filter by</Typography>
            </Box>
            {filter.status && (
              <FilterForm
                filter={filter}
                setFilter={setFilter}
                handleSearch={(data) => {
                  if (data?.data) {
                    getUsers({
                      name: data.data.full_name,
                      email: data.data.email,
                      roles: data.data.roles,
                      status: data.data.status,
                      permission: data.data.permissions,
                      ...(order.orderBy
                        ? {
                          sortBy: order.orderBy,
                          sortOrder: order.order,
                        }
                        : {}),
                      page: 1,
                      perPage: paginationData.rowsPerPage
                    })
                  } else {
                    getUsers({
                      name: filter.data.full_name,
                      email: filter.data.email,
                      roles: filter.data.roles,
                      status: filter.data.status,
                      permission: filter.data.permissions,
                      ...(order.orderBy
                        ? {
                          sortBy: order.orderBy,
                          sortOrder: order.order,
                        }
                        : {}),
                      page: 1,
                      perPage: paginationData.rowsPerPage,
                    });
                  }
                }}
              />
            )}
          </Box>
        </EnhancedTable>
      </Box>
    </>
  );
}
